// ============================================================
// Payroll Page — 薪資結算
// 月薪明細 + 加班費(§24) + 扣款 + 勞健保
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { exportCSV } from '../lib/csvExport';
import { shiftMonth, taipeiThisMonth } from '../lib/datetime';

interface PayrollEntry {
  userId: string;
  userName: string;
  userAvatar: string | null;
  role: string;
  payType: string;
  employmentType: string;
  hourlyRate: number;
  monthlySalary: number;
  workDays: number;
  scheduledDays: number;
  totalWorkHours: number;
  regularHours: number;
  overtimeHours: number;
  basePay: number;
  overtimePay: number;
  grossPay: number;
  lateCount: number;
  lateDeduct: number;
  leaveDeduct: number;
  laborIns: number;
  healthIns: number;
  totalDeductions: number;
  netPay: number;
  holidayBonus?: number;
  adjustmentBonus?: number;
  adjustmentDeduction?: number;
  adjustmentItems?: AdjustmentItem[];
  // 勞健保自動計算「原始值」+ 是否被覆寫
  autoLaborIns?: number;
  autoHealthIns?: number;
  laborInsOverridden?: boolean;
  healthInsOverridden?: boolean;
  /** 結算前警示：有排班沒打卡、沒打下班卡、打卡還沒核准 */
  warnings?: string[];
}

interface AdjustmentItem {
  id: string;
  userId: string;
  month: string;
  category: 'BONUS' | 'DEDUCTION' | 'OVERRIDE_LABOR_INS' | 'OVERRIDE_HEALTH_INS';
  label: string;
  amount: number;
  note?: string;
  /** 每月固定：結算時自動帶到下個月 */
  recurring?: boolean;
  createdAt: string;
}

interface LawRates {
  minWageMonthly: number;
  minWageHourly: number;
  laborInsRate: number;
  employmentInsRate: number;
  healthInsRate: number;
  laborWorkerShare: number;
  healthWorkerShare: number;
}

interface MonthStatus {
  status: 'DRAFT' | 'CONFIRMED' | 'PAID';
  confirmedAt: string | null;
  confirmedBy: string | null;
  paidAt: string | null;
  paidBy: string | null;
  paidNote: string | null;
}

interface PayrollData {
  month: string;
  payrolls: PayrollEntry[];
  monthStatus?: MonthStatus;
  /** 上個月還沒帶進本月的「每月固定」項目筆數 */
  prevRecurringCount?: number;
  /** 法規提醒（假日沒匯入、基本工資沒確認） */
  notices?: string[];
  /** 每月自動法規檢查：有變動、還沒看過的報告 */
  lawWatch?: { id: string; checkedAt: string; summary: string } | null;
  lawRates?: LawRates;
  summary: {
    staffCount: number;
    totalGrossPay: number;
    totalNetPay: number;
    totalOvertimePay: number;
    totalDeductions: number;
    totalWorkHours: number;
    totalOvertimeHours: number;
    /** 後端本來就有回傳，但前端型別漏了 → 摘要對不起來 */
    totalHolidayBonus: number;
  };
}

export function PayrollPage() {
  const [month, setMonth] = useState(taipeiThisMonth);
  const [data, setData] = useState<PayrollData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [adjustingUser, setAdjustingUser] = useState<PayrollEntry | null>(null);

  const handleExportCSV = () => {
    if (!data) return;
    const headers = [
      '員工', '類型', '出勤天數', '總工時', '正常工時', '加班時數',
      '底薪', '加班費', '國定假日加倍', '獎金調整', '應付小計',
      '勞保', '健保', '遲到扣款', '請假扣薪', '扣款調整', '扣除小計',
      '實付金額',
    ];
    const rows = data.payrolls.map(p => [
      p.userName,
      p.payType === 'MONTHLY' ? '月薪' : '時薪',
      p.workDays,
      p.totalWorkHours,
      p.regularHours,
      p.overtimeHours,
      p.basePay,
      p.overtimePay,
      p.holidayBonus ?? 0,
      p.adjustmentBonus ?? 0,
      p.grossPay,
      p.laborIns,
      p.healthIns,
      p.lateDeduct,
      p.leaveDeduct,
      p.adjustmentDeduction ?? 0,
      p.totalDeductions,
      p.netPay,
    ]);
    exportCSV(`薪資結算_${month}.csv`, headers, rows);
  };

  const fetchPayroll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<PayrollData>(`/payroll?month=${month}`);
      setData(res);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { fetchPayroll(); }, [fetchPayroll]);

  const navigateMonth = (delta: number) => setMonth(m => shiftMonth(m, delta));

  const [y, m] = month.split('-').map(Number);
  const monthLabel = `${y} 年 ${m} 月`;

  const status = data?.monthStatus?.status ?? 'DRAFT';
  const isLocked = status !== 'DRAFT';

  const handleConfirm = async () => {
    if (!data) return;
    const ok = window.confirm(
      `確認結算 ${monthLabel} 的薪資？\n\n` +
      `員工 ${data.summary.staffCount} 人，實付總額 $${data.summary.totalNetPay.toLocaleString()}\n\n` +
      `結算後將鎖定，避免誤改。可在「重新編輯」後再次調整。`
    );
    if (!ok) return;
    try {
      const r = await api.post<{ carriedOver?: number }>('/payroll/confirm', { month });
      await fetchPayroll();
      if (r.carriedOver) alert(`已把 ${r.carriedOver} 筆「每月固定」項目帶到下個月。`);
    } catch (err) {
      alert('結算失敗：' + (err as Error).message);
    }
  };

  const [applying, setApplying] = useState(false);
  const handleApplyRecurring = async () => {
    setApplying(true);
    try {
      const r = await api.post<{ copied: number }>('/payroll/apply-recurring', { month });
      await fetchPayroll();
      alert(r.copied > 0 ? `已帶入 ${r.copied} 筆上個月的固定項目。` : '沒有需要帶入的項目。');
    } catch (err) {
      alert('帶入失敗：' + (err as Error).message);
    } finally {
      setApplying(false);
    }
  };

  const handleAckLawWatch = async (id: string) => {
    try {
      await api.post(`/law-params/watch/${id}/ack`);
      await fetchPayroll();
    } catch (err) {
      alert('操作失敗：' + (err as Error).message);
    }
  };

  const handleMarkPaid = async () => {
    const note = window.prompt('請輸入支付備註（選填，例如：銀行轉帳完成 / 現金支付）：') ?? '';
    try {
      await api.post('/payroll/mark-paid', { month, note });
      await fetchPayroll();
    } catch (err) {
      alert('標記失敗：' + (err as Error).message);
    }
  };

  const handleReopen = async () => {
    const ok = window.confirm('重新開放本月編輯？\n\n結算與支付狀態都會清除。');
    if (!ok) return;
    try {
      await api.post('/payroll/reopen', { month });
      await fetchPayroll();
    } catch (err) {
      alert('操作失敗：' + (err as Error).message);
    }
  };

  const fmtDateTime = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
      <h1 className="text-xl font-bold text-morandi-800">薪資結算</h1>

      {/* 月份選擇 + 匯出 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigateMonth(-1)} className="w-9 h-9 rounded-full flex items-center justify-center text-morandi-500 hover:bg-morandi-100">‹</button>
          <span className="text-lg font-semibold text-morandi-700 min-w-[120px] text-center">{monthLabel}</span>
          <button onClick={() => navigateMonth(1)} className="w-9 h-9 rounded-full flex items-center justify-center text-morandi-500 hover:bg-morandi-100">›</button>
        </div>
        {data && (
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 text-sm bg-earth-400 hover:bg-earth-500 text-white rounded-btn transition-colors"
          >
            ↓ 匯出 CSV
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-10 text-morandi-400">載入中...</div>
      ) : data ? (
        <>
          {/* 每月自動法規檢查：有變動才出現，老闆看過按掉 */}
          {data.lawWatch && (
            <div className="rounded-card p-4 border border-rose-300 bg-rose-50 space-y-2">
              <div className="font-semibold text-rose-600">⚠ 法規可能有變動，請確認</div>
              <div className="text-sm text-rose-700 whitespace-pre-line">{data.lawWatch.summary}</div>
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-rose-400">
                <span>{fmtDateTime(data.lawWatch.checkedAt)} 自動檢查。要改數字請到「門店設定 → 法規數字」。</span>
                <button
                  onClick={() => handleAckLawWatch(data.lawWatch!.id)}
                  className="px-3 py-1.5 bg-white border border-rose-300 text-rose-500 hover:bg-rose-100 rounded-btn"
                >我看過了</button>
              </div>
            </div>
          )}

          {(data.notices?.length ?? 0) > 0 && (
            <div className="rounded-card p-4 border border-amber-300 bg-amber-50 space-y-1">
              {data.notices!.map(n => (
                <div key={n} className="text-sm text-amber-700">⚠ {n}</div>
              ))}
            </div>
          )}

          {/* 結算狀態列 */}
          <div className={`rounded-card p-4 flex flex-wrap items-center justify-between gap-3 border
            ${status === 'PAID' ? 'bg-sage-50 border-sage-200' :
              status === 'CONFIRMED' ? 'bg-blue-50 border-blue-200' :
              'bg-morandi-50 border-morandi-200'}`}>
            <div className="flex items-center gap-3">
              <span className={`text-xs px-2 py-1 rounded-full font-medium
                ${status === 'PAID' ? 'bg-sage-500 text-white' :
                  status === 'CONFIRMED' ? 'bg-blue-500 text-white' :
                  'bg-morandi-300 text-white'}`}>
                {status === 'PAID' ? '✓ 已實付' : status === 'CONFIRMED' ? '✓ 已結算' : '草稿'}
              </span>
              <div className="text-xs text-morandi-500">
                {status === 'DRAFT' && '可自由調整薪資項目，確認後再結算'}
                {status === 'CONFIRMED' && data.monthStatus?.confirmedAt &&
                  `${fmtDateTime(data.monthStatus.confirmedAt)} 結算 · 等待支付`}
                {status === 'PAID' && data.monthStatus?.paidAt && (
                  <>
                    {fmtDateTime(data.monthStatus.paidAt)} 支付完成
                    {data.monthStatus.paidNote && <span className="ml-2 text-morandi-400">· {data.monthStatus.paidNote}</span>}
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {status === 'DRAFT' && (data.prevRecurringCount ?? 0) > 0 && (
                <button
                  onClick={handleApplyRecurring}
                  disabled={applying}
                  className="px-4 py-2 text-sm bg-earth-400 hover:bg-earth-500 text-white rounded-btn transition-colors disabled:opacity-50"
                >{applying ? '帶入中…' : `帶入上月固定項目（${data.prevRecurringCount} 筆）`}</button>
              )}
              {status === 'DRAFT' && (
                <button
                  onClick={handleConfirm}
                  className="px-4 py-2 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded-btn transition-colors"
                >✓ 確認結算</button>
              )}
              {status === 'CONFIRMED' && (
                <>
                  <button
                    onClick={handleReopen}
                    className="px-3 py-2 text-sm bg-white border border-morandi-300 text-morandi-600 hover:bg-morandi-100 rounded-btn"
                  >重新編輯</button>
                  <button
                    onClick={handleMarkPaid}
                    className="px-4 py-2 text-sm bg-sage-500 hover:bg-sage-600 text-white rounded-btn transition-colors"
                  >💰 標記已實付</button>
                </>
              )}
              {status === 'PAID' && (
                <button
                  onClick={handleReopen}
                  className="px-3 py-2 text-sm bg-white border border-rose-300 text-rose-500 hover:bg-rose-50 rounded-btn"
                >重新編輯</button>
              )}
            </div>
          </div>

          {/* 總摘要 */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <SummaryCard label="應付總額" value={data.summary.totalGrossPay} />
            <SummaryCard label="加班費" value={data.summary.totalOvertimePay} highlight={data.summary.totalOvertimePay > 0} />
            <SummaryCard label="假日加倍" value={data.summary.totalHolidayBonus ?? 0} highlight={(data.summary.totalHolidayBonus ?? 0) > 0} />
            <SummaryCard label="扣除總額" value={data.summary.totalDeductions} />
            <SummaryCard label="實付總額" value={data.summary.totalNetPay} primary />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <MiniStat label="員工數" value={`${data.summary.staffCount} 人`} />
            <MiniStat label="總工時" value={`${data.summary.totalWorkHours} hr`} />
            <MiniStat label="加班時數" value={`${data.summary.totalOvertimeHours} hr`} />
          </div>

          {/* 員工薪資表 */}
          <div className="card-morandi overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-morandi-50 text-morandi-500 text-xs">
                  <th className="text-left px-4 py-3 font-medium">員工</th>
                  <th className="text-center px-2 py-3 font-medium">類型</th>
                  <th className="text-center px-2 py-3 font-medium">出勤</th>
                  <th className="text-center px-2 py-3 font-medium">工時</th>
                  <th className="text-right px-2 py-3 font-medium">底薪</th>
                  <th className="text-right px-2 py-3 font-medium">加班費</th>
                  <th className="text-right px-2 py-3 font-medium">加項</th>
                  <th className="text-right px-2 py-3 font-medium">扣除</th>
                  <th className="text-right px-4 py-3 font-medium">實付</th>
                  <th className="text-center px-2 py-3 font-medium">操作</th>
                </tr>
              </thead>
              <tbody>
                {data.payrolls.map(p => {
                  const isExpanded = expandedId === p.userId;
                  return (
                    <React.Fragment key={p.userId}>
                      <tr
                        className="border-t border-morandi-100 hover:bg-morandi-50 cursor-pointer transition-colors"
                        onClick={() => setExpandedId(isExpanded ? null : p.userId)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-earth-100 flex items-center justify-center text-earth-600 text-xs font-medium shrink-0">
                              {p.userName.slice(0, 1)}
                            </div>
                            <div>
                              <div className="font-medium text-morandi-700">{p.userName}</div>
                            </div>
                            <span className="text-morandi-300 text-xs">{isExpanded ? '▲' : '▼'}</span>
                          </div>
                        </td>
                        <td className="text-center px-2 py-3">
                          <span className={`badge text-xs ${p.payType === 'MONTHLY' ? 'bg-earth-100 text-earth-600' : 'bg-smoke-100 text-smoke-600'}`}>
                            {p.payType === 'MONTHLY' ? '月薪' : '時薪'}
                          </span>
                        </td>
                        <td className="text-center px-2 py-3 font-mono text-xs">{p.workDays}天</td>
                        <td className="text-center px-2 py-3 font-mono text-xs">
                          {p.totalWorkHours}
                          {p.overtimeHours > 0 && <span className="text-rose-400 ml-0.5">(+{p.overtimeHours})</span>}
                        </td>
                        <td className="text-right px-2 py-3 font-mono">${p.basePay.toLocaleString()}</td>
                        <td className="text-right px-2 py-3 font-mono text-amber-600">{p.overtimePay > 0 ? `$${p.overtimePay.toLocaleString()}` : '-'}</td>
                        {/* 加項＝國定假日加倍 + 獎金調整。少了這欄，
                            「底薪 + 加班費 − 扣除」永遠湊不出「實付」，
                            表格一眼看起來就像算錯。 */}
                        <td className="text-right px-2 py-3 font-mono text-amber-600">
                          {(() => {
                            const extra = (p.holidayBonus ?? 0) + (p.adjustmentBonus ?? 0);
                            return extra > 0 ? `+$${extra.toLocaleString()}` : '-';
                          })()}
                        </td>
                        <td className="text-right px-2 py-3 font-mono text-rose-500">{p.totalDeductions > 0 ? `-$${p.totalDeductions.toLocaleString()}` : '-'}</td>
                        <td className="text-right px-4 py-3 font-mono font-bold text-morandi-800">${p.netPay.toLocaleString()}</td>
                        <td className="text-center px-2 py-3">
                          <button
                            onClick={(e) => { e.stopPropagation(); setAdjustingUser(p); }}
                            disabled={isLocked}
                            title={isLocked ? '已結算，請先「重新編輯」' : ''}
                            className="text-xs text-earth-500 hover:text-earth-700 px-2 py-1 rounded hover:bg-earth-50 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            {isLocked ? '🔒 鎖定' : '＋ 調整'}
                          </button>
                        </td>
                      </tr>

                      {/* 結算前警示：不用展開就看得到 */}
                      {(p.warnings?.length ?? 0) > 0 && (
                        <tr className="bg-rose-50/50">
                          <td colSpan={10} className="px-4 pb-2 pt-0">
                            {p.warnings!.map(w => (
                              <div key={w} className="text-xs text-rose-500">⚠ {w}</div>
                            ))}
                          </td>
                        </tr>
                      )}

                      {/* 展開明細 */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={9} className="bg-morandi-50 px-6 py-4">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs">
                              {/* 收入 */}
                              <div>
                                <div className="font-semibold text-morandi-600 mb-2">收入明細</div>
                                <div className="space-y-1">
                                  {p.payType === 'MONTHLY' ? (
                                    <DetailRow label="月薪" value={`$${p.monthlySalary.toLocaleString()}`} />
                                  ) : (
                                    <>
                                      <DetailRow label="時薪" value={`$${p.hourlyRate}/hr`} />
                                      <DetailRow label="正常工時" value={`${p.regularHours} hr`} />
                                      <DetailRow label="底薪" value={`$${p.basePay.toLocaleString()}`} />
                                    </>
                                  )}
                                  {p.overtimeHours > 0 && (
                                    <>
                                      <DetailRow label="加班時數" value={`${p.overtimeHours} hr`} />
                                      <DetailRow label="加班費 (§24)" value={`$${p.overtimePay.toLocaleString()}`} highlight />
                                    </>
                                  )}
                                  {(p.holidayBonus ?? 0) > 0 && (
                                    <DetailRow label="國定假日加倍 (§39)" value={`+$${(p.holidayBonus ?? 0).toLocaleString()}`} highlight />
                                  )}
                                  {p.adjustmentItems?.filter(a => a.category === 'BONUS').map(a => (
                                    <DetailRow key={a.id} label={`🎁 ${a.label}`} value={`+$${a.amount.toLocaleString()}`} highlight />
                                  ))}
                                  <div className="border-t border-morandi-200 pt-1 mt-1">
                                    <DetailRow label="應付小計" value={`$${p.grossPay.toLocaleString()}`} bold />
                                  </div>
                                </div>
                              </div>

                              {/* 扣除 */}
                              <div>
                                <div className="font-semibold text-morandi-600 mb-2">扣除明細</div>
                                <div className="space-y-1">
                                  <DetailRow
                                    label={`勞保費${p.laborInsOverridden ? ' ✏' : ''}`}
                                    value={p.laborIns > 0 ? `-$${p.laborIns}` : '-'}
                                  />
                                  <DetailRow
                                    label={`健保費${p.healthInsOverridden ? ' ✏' : ''}`}
                                    value={p.healthIns > 0 ? `-$${p.healthIns}` : '-'}
                                  />
                                  {p.lateDeduct > 0 && (
                                    <DetailRow label={`遲到扣款 (${p.lateCount}次)`} value={`-$${p.lateDeduct}`} warn />
                                  )}
                                  {p.leaveDeduct > 0 && (
                                    <DetailRow label="事假/無薪假扣薪" value={`-$${p.leaveDeduct}`} warn />
                                  )}
                                  {p.adjustmentItems?.filter(a => a.category === 'DEDUCTION').map(a => (
                                    <DetailRow key={a.id} label={`📉 ${a.label}`} value={`-$${a.amount.toLocaleString()}`} warn />
                                  ))}
                                  <div className="border-t border-morandi-200 pt-1 mt-1">
                                    <DetailRow label="扣除小計" value={p.totalDeductions > 0 ? `-$${p.totalDeductions.toLocaleString()}` : '$0'} bold />
                                  </div>
                                </div>
                              </div>

                              {/* 出勤 */}
                              <div>
                                <div className="font-semibold text-morandi-600 mb-2">出勤統計</div>
                                <div className="space-y-1">
                                  <DetailRow label="排班天數" value={`${p.scheduledDays} 天`} />
                                  <DetailRow label="實際出勤" value={`${p.workDays} 天`} />
                                  <DetailRow label="總工時" value={`${p.totalWorkHours} hr`} />
                                  <DetailRow label="加班時數" value={`${p.overtimeHours} hr`} />
                                  {p.lateCount > 0 && <DetailRow label="遲到次數" value={`${p.lateCount} 次`} warn />}
                                </div>
                                <div className="border-t border-morandi-200 pt-2 mt-2">
                                  <div className="flex justify-between items-center">
                                    <span className="font-semibold text-morandi-700">實付金額</span>
                                    <span className="text-lg font-bold font-mono text-sage-600">${p.netPay.toLocaleString()}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* 勞基法說明 */}
          <div className="text-xs text-morandi-300 space-y-0.5 px-2">
            <p>* 加班費依勞基法 §24 計算：前 2 小時 x1.34、第 3-4 小時 x1.67</p>
            {data.lawRates && (
              <p>
                * 本月基本工資 ${data.lawRates.minWageMonthly.toLocaleString()}；
                勞保費率 {pct(data.lawRates.laborInsRate)}
                {data.lawRates.employmentInsRate > 0 && `＋就業保險 ${pct(data.lawRates.employmentInsRate)}`}
                （勞方 {pct(data.lawRates.laborWorkerShare)}）、健保費率 {pct(data.lawRates.healthInsRate)}（勞方 {pct(data.lawRates.healthWorkerShare)}）
              </p>
            )}
            <p>* 月薪制員工底薪固定，時薪制依實際工時計算</p>
          </div>
        </>
      ) : (
        <div className="text-center py-10 text-morandi-300">無資料</div>
      )}

      {adjustingUser && (
        <AdjustmentModal
          user={adjustingUser}
          month={month}
          onClose={() => setAdjustingUser(null)}
          onChanged={() => fetchPayroll()}
        />
      )}
    </div>
  );
}

function AdjustmentModal({ user, month, onClose, onChanged }: {
  user: PayrollEntry;
  month: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [items, setItems] = useState<AdjustmentItem[]>(user.adjustmentItems ?? []);
  const [category, setCategory] = useState<'BONUS' | 'DEDUCTION'>('BONUS');
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  // 每月固定：新增項目預設不固定；勞健保覆寫幾乎每月都一樣，預設固定
  const [recurring, setRecurring] = useState(false);
  const [insRecurring, setInsRecurring] = useState(true);

  // 勞健保覆寫：input 預設帶當前生效值
  const [laborInsInput, setLaborInsInput] = useState(String(user.laborIns ?? ''));
  const [healthInsInput, setHealthInsInput] = useState(String(user.healthIns ?? ''));
  const autoLabor = user.autoLaborIns ?? user.laborIns;
  const autoHealth = user.autoHealthIns ?? user.healthIns;

  const refresh = async () => {
    const r = await api.get<{ adjustments: AdjustmentItem[] }>(
      `/payroll-adjustments?month=${month}&userId=${user.userId}`
    );
    setItems(r.adjustments);
  };

  // 覆寫勞健保：刪掉同類所有舊紀錄、再新增一筆
  const overrideInsurance = async (kind: 'OVERRIDE_LABOR_INS' | 'OVERRIDE_HEALTH_INS', value: string) => {
    if (value === '') { alert('請填金額'); return; }
    const amt = Number(value);
    if (!Number.isFinite(amt) || amt < 0) { alert('金額需為非負數'); return; }
    setBusy(true);
    try {
      // 刪掉同 category 的舊覆寫（保持「最新一筆生效」乾淨）
      const old = items.filter(i => i.category === kind);
      for (const o of old) {
        await api.delete(`/payroll-adjustments/${o.id}`);
      }
      await api.post('/payroll-adjustments', {
        userId: user.userId,
        month,
        category: kind,
        label: kind === 'OVERRIDE_LABOR_INS' ? '勞保覆寫' : '健保覆寫',
        amount: amt,
        recurring: insRecurring,
      });
      await refresh();
      onChanged();
    } catch (err) {
      alert('覆寫失敗：' + (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const restoreInsuranceAuto = async (kind: 'OVERRIDE_LABOR_INS' | 'OVERRIDE_HEALTH_INS') => {
    setBusy(true);
    try {
      const old = items.filter(i => i.category === kind);
      for (const o of old) {
        await api.delete(`/payroll-adjustments/${o.id}`);
      }
      await refresh();
      onChanged();
      // input 還原成自動值
      if (kind === 'OVERRIDE_LABOR_INS') setLaborInsInput(String(autoLabor));
      else setHealthInsInput(String(autoHealth));
    } catch (err) {
      alert('還原失敗：' + (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    if (!label.trim() || !amount) { alert('請填項目與金額'); return; }
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) { alert('金額需為正數'); return; }
    setBusy(true);
    try {
      await api.post('/payroll-adjustments', {
        userId: user.userId,
        month,
        category,
        label: label.trim(),
        amount: amt,
        note: note.trim() || undefined,
        recurring,
      });
      setLabel(''); setAmount(''); setNote(''); setRecurring(false);
      await refresh();
      onChanged();
    } catch (err) {
      alert('新增失敗：' + (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const toggleRecurring = async (it: AdjustmentItem) => {
    try {
      await api.put(`/payroll-adjustments/${it.id}`, { recurring: !it.recurring });
      await refresh();
      onChanged();
    } catch (err) {
      alert('更新失敗：' + (err as Error).message);
    }
  };

  const laborOverrideItem = items.find(i => i.category === 'OVERRIDE_LABOR_INS');
  const healthOverrideItem = items.find(i => i.category === 'OVERRIDE_HEALTH_INS');

  const handleDelete = async (id: string) => {
    if (!confirm('確定刪除此項調整？')) return;
    try {
      await api.delete(`/payroll-adjustments/${id}`);
      await refresh();
      onChanged();
    } catch (err) {
      alert('刪除失敗：' + (err as Error).message);
    }
  };

  // 編輯既有項目
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editNote, setEditNote] = useState('');

  const startEdit = (it: AdjustmentItem) => {
    setEditingId(it.id);
    setEditLabel(it.label);
    setEditAmount(String(it.amount));
    setEditNote(it.note ?? '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditLabel(''); setEditAmount(''); setEditNote('');
  };

  const saveEdit = async (id: string) => {
    if (!editLabel.trim() || !editAmount) { alert('項目與金額不可空'); return; }
    const amt = Number(editAmount);
    if (!Number.isFinite(amt) || amt <= 0) { alert('金額需為正數'); return; }
    try {
      await api.put(`/payroll-adjustments/${id}`, {
        label: editLabel.trim(),
        amount: amt,
        note: editNote.trim() || null,
      });
      cancelEdit();
      await refresh();
      onChanged();
    } catch (err) {
      alert('儲存失敗：' + (err as Error).message);
    }
  };

  const bonusItems = items.filter(i => i.category === 'BONUS');
  const deductionItems = items.filter(i => i.category === 'DEDUCTION');
  const bonusTotal = bonusItems.reduce((s, i) => s + i.amount, 0);
  const deductionTotal = deductionItems.reduce((s, i) => s + i.amount, 0);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div>
          <h2 className="text-lg font-bold text-morandi-800">
            薪資調整 · {user.userName}
          </h2>
          <div className="text-xs text-morandi-400">{month}</div>
        </div>

        {/* 勞保 / 健保覆寫 */}
        <div className="bg-morandi-50 rounded-lg p-3 space-y-2">
          <div className="text-sm font-semibold text-morandi-700">保險自付額（手動調整）</div>
          <div className="text-xs text-morandi-400">
            空白填入金額後按「套用」可覆寫；按「還原」回到自動算的值。
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-morandi-600 w-12">勞保</span>
              {laborOverrideItem && (
                <RecurringChip on={!!laborOverrideItem.recurring} onClick={() => toggleRecurring(laborOverrideItem)} />
              )}
              <span className="text-xs text-morandi-400 w-24">自動 ${autoLabor}</span>
              <input
                type="number"
                value={laborInsInput}
                onChange={e => setLaborInsInput(e.target.value)}
                className="input-field text-sm flex-1"
              />
              <button
                onClick={() => overrideInsurance('OVERRIDE_LABOR_INS', laborInsInput)}
                disabled={busy}
                className="text-xs bg-earth-400 hover:bg-earth-500 text-white rounded px-3 py-1.5 disabled:opacity-50"
              >套用</button>
              {user.laborInsOverridden && (
                <button
                  onClick={() => restoreInsuranceAuto('OVERRIDE_LABOR_INS')}
                  disabled={busy}
                  className="text-xs text-rose-500 hover:text-rose-700 px-1 disabled:opacity-50"
                >還原</button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-morandi-600 w-12">健保</span>
              {healthOverrideItem && (
                <RecurringChip on={!!healthOverrideItem.recurring} onClick={() => toggleRecurring(healthOverrideItem)} />
              )}
              <span className="text-xs text-morandi-400 w-24">自動 ${autoHealth}</span>
              <input
                type="number"
                value={healthInsInput}
                onChange={e => setHealthInsInput(e.target.value)}
                className="input-field text-sm flex-1"
              />
              <button
                onClick={() => overrideInsurance('OVERRIDE_HEALTH_INS', healthInsInput)}
                disabled={busy}
                className="text-xs bg-earth-400 hover:bg-earth-500 text-white rounded px-3 py-1.5 disabled:opacity-50"
              >套用</button>
              {user.healthInsOverridden && (
                <button
                  onClick={() => restoreInsuranceAuto('OVERRIDE_HEALTH_INS')}
                  disabled={busy}
                  className="text-xs text-rose-500 hover:text-rose-700 px-1 disabled:opacity-50"
                >還原</button>
              )}
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-morandi-500 pt-1">
            <input type="checkbox" checked={insRecurring} onChange={e => setInsRecurring(e.target.checked)} />
            套用的金額「每月固定」（下個月自動帶入，不用再打）
          </label>
        </div>

        {/* 現有項目 */}
        {items.length > 0 && (
          <div className="space-y-2">
            {bonusItems.length > 0 && (
              <div>
                <div className="text-xs text-sage-600 mb-1">🎁 獎金（+${bonusTotal.toLocaleString()}）</div>
                {bonusItems.map(i => (
                  <ItemRow
                    key={i.id}
                    item={i}
                    sign="+"
                    color="text-sage-600"
                    editing={editingId === i.id}
                    editLabel={editLabel} setEditLabel={setEditLabel}
                    editAmount={editAmount} setEditAmount={setEditAmount}
                    editNote={editNote} setEditNote={setEditNote}
                    onStartEdit={() => startEdit(i)}
                    onSave={() => saveEdit(i.id)}
                    onCancel={cancelEdit}
                    onDelete={() => handleDelete(i.id)}
                    onToggleRecurring={() => toggleRecurring(i)}
                  />
                ))}
              </div>
            )}

            {deductionItems.length > 0 && (
              <div>
                <div className="text-xs text-rose-500 mb-1">📉 扣款（-${deductionTotal.toLocaleString()}）</div>
                {deductionItems.map(i => (
                  <ItemRow
                    key={i.id}
                    item={i}
                    sign="-"
                    color="text-rose-500"
                    editing={editingId === i.id}
                    editLabel={editLabel} setEditLabel={setEditLabel}
                    editAmount={editAmount} setEditAmount={setEditAmount}
                    editNote={editNote} setEditNote={setEditNote}
                    onStartEdit={() => startEdit(i)}
                    onSave={() => saveEdit(i.id)}
                    onCancel={cancelEdit}
                    onDelete={() => handleDelete(i.id)}
                    onToggleRecurring={() => toggleRecurring(i)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* 新增表單 */}
        <div className="border-t border-morandi-200 pt-4 space-y-3">
          <div className="text-sm font-semibold text-morandi-700">新增項目</div>

          <div className="flex gap-2">
            <button
              onClick={() => setCategory('BONUS')}
              className={`flex-1 py-2 rounded-btn text-sm transition-colors
                ${category === 'BONUS' ? 'bg-sage-400 text-white' : 'bg-morandi-100 text-morandi-500'}`}
            >🎁 獎金</button>
            <button
              onClick={() => setCategory('DEDUCTION')}
              className={`flex-1 py-2 rounded-btn text-sm transition-colors
                ${category === 'DEDUCTION' ? 'bg-rose-400 text-white' : 'bg-morandi-100 text-morandi-500'}`}
            >📉 扣款</button>
          </div>

          <div>
            <label className="text-xs text-morandi-500 block mb-1">項目名稱</label>
            <input
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder={category === 'BONUS' ? '例：年終獎金、全勤獎、績效獎金' : '例：預支薪資、制服費、遲到罰款'}
              className="input-field"
            />
          </div>

          <div>
            <label className="text-xs text-morandi-500 block mb-1">金額（新台幣）</label>
            <input
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="例：3000"
              className="input-field"
            />
          </div>

          <div>
            <label className="text-xs text-morandi-500 block mb-1">備註（選填）</label>
            <input
              type="text"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="說明用途"
              className="input-field"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-morandi-600">
            <input type="checkbox" checked={recurring} onChange={e => setRecurring(e.target.checked)} />
            每月固定（下個月自動帶入，例如健保補助）
          </label>

          <button
            onClick={handleAdd}
            disabled={busy || !label.trim() || !amount}
            className="w-full py-2.5 bg-earth-400 hover:bg-earth-500 disabled:opacity-50 text-white rounded-btn text-sm"
          >
            {busy ? '儲存中...' : '新增'}
          </button>
        </div>

        <button
          onClick={onClose}
          className="w-full py-2 text-sm text-morandi-500 hover:bg-morandi-100 rounded-btn"
        >關閉</button>
      </div>
    </div>
  );
}

// 調整項目單列（檢視 / 編輯切換）
function ItemRow({ item, sign, color, editing, editLabel, setEditLabel, editAmount, setEditAmount, editNote, setEditNote, onStartEdit, onSave, onCancel, onDelete, onToggleRecurring }: {
  item: AdjustmentItem;
  sign: '+' | '-';
  color: string;
  editing: boolean;
  editLabel: string; setEditLabel: (s: string) => void;
  editAmount: string; setEditAmount: (s: string) => void;
  editNote: string; setEditNote: (s: string) => void;
  onStartEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onToggleRecurring: () => void;
}) {
  if (editing) {
    return (
      <div className="py-2 px-2 rounded bg-morandi-50 space-y-1.5">
        <div className="grid grid-cols-2 gap-2">
          <input
            value={editLabel}
            onChange={e => setEditLabel(e.target.value)}
            placeholder="項目"
            maxLength={30}
            className="input-field text-sm"
          />
          <input
            type="number"
            value={editAmount}
            onChange={e => setEditAmount(e.target.value)}
            placeholder="金額"
            className="input-field text-sm font-mono"
          />
        </div>
        <input
          value={editNote}
          onChange={e => setEditNote(e.target.value)}
          placeholder="備註（選填）"
          maxLength={200}
          className="input-field text-xs"
        />
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="text-xs text-morandi-500 hover:text-morandi-700 px-2 py-1">取消</button>
          <button onClick={onSave} className="text-xs bg-earth-400 hover:bg-earth-500 text-white rounded px-3 py-1">儲存</button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-morandi-50">
      <div>
        <div className="text-sm text-morandi-700 flex items-center gap-1.5">
          {item.label}
          <RecurringChip on={!!item.recurring} onClick={onToggleRecurring} />
        </div>
        {item.note && <div className="text-xs text-morandi-400">{item.note}</div>}
      </div>
      <div className="flex items-center gap-3">
        <span className={`text-sm font-mono ${color}`}>{sign}${item.amount.toLocaleString()}</span>
        <button onClick={onStartEdit} className="text-xs text-earth-500 hover:text-earth-700">編輯</button>
        <button onClick={onDelete} className="text-xs text-rose-400 hover:text-rose-600">刪除</button>
      </div>
    </div>
  );
}

/** 「每月固定」小標籤：點一下切換 */
function RecurringChip({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={on ? '每月固定：下個月會自動帶入。點一下取消' : '點一下設為每月固定'}
      className={`text-[11px] px-1.5 py-0.5 rounded-full border whitespace-nowrap
        ${on ? 'bg-earth-100 border-earth-300 text-earth-600' : 'bg-white border-morandi-200 text-morandi-300'}`}
    >{on ? '每月固定' : '單月'}</button>
  );
}

/** 0.115 → '11.5%' */
const pct = (r: number) => `${Math.round(r * 10000) / 100}%`;

function SummaryCard({ label, value, highlight, primary }: {
  label: string; value: number; highlight?: boolean; primary?: boolean;
}) {
  return (
    <div className={`card-morandi p-3 text-center ${highlight ? 'ring-1 ring-amber-300' : ''} ${primary ? 'ring-1 ring-sage-400' : ''}`}>
      <div className={`text-xl font-bold font-mono ${primary ? 'text-sage-600' : highlight ? 'text-amber-600' : 'text-morandi-700'}`}>
        ${value.toLocaleString()}
      </div>
      <div className="text-xs text-morandi-400 mt-0.5">{label}</div>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card-morandi p-2.5 text-center">
      <div className="text-sm font-medium font-mono text-morandi-700">{value}</div>
      <div className="text-xs text-morandi-400">{label}</div>
    </div>
  );
}

function DetailRow({ label, value, bold, highlight, warn }: {
  label: string; value: string; bold?: boolean; highlight?: boolean; warn?: boolean;
}) {
  return (
    <div className="flex justify-between items-center">
      <span className={`text-morandi-500 ${bold ? 'font-semibold' : ''}`}>{label}</span>
      <span className={`font-mono ${bold ? 'font-bold text-morandi-700' : ''} ${highlight ? 'text-amber-600 font-medium' : ''} ${warn ? 'text-rose-500' : ''}`}>
        {value}
      </span>
    </div>
  );
}
